<template>
        <div
          class="kanban-column"
          :class="{ minimized: isMinimized }"
          :style="{ '--column-color': column.cor || '#4f46e5' }"
          :data-column-id="column.id"
        >
    <!-- Header da Coluna -->
    <div class="column-header" @contextmenu.prevent="handleRightClick">
      <div v-if="!isMinimized" class="column-title">
        <!-- Ícone para arrastar a coluna (apenas se não for coluna padrão) -->
        <div 
          v-if="!column.isPadrao && canMoveColumns" 
          class="column-drag-handle" 
          :title="t('kanban.column.dragColumn')"
        >
          <svg class="w-3 h-3 opacity-40 hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 5h2v2H9V5zm0 4h2v2H9V9zm0 4h2v2H9v-2zm0 4h2v2H9v-2zm4-12h2v2h-2V5zm0 4h2v2h-2V9zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2z"/>
          </svg>
        </div>
        <h3 class="text-white font-semibold">{{ column.nome }}</h3>
        <span
          v-if="hasColumnUnreadMessages"
          class="column-unread-indicator"
          :title="columnUnreadTitle"
          :aria-label="columnUnreadTitle"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
            <path d="M7 9h10v2H7zm0-3h7v2H7z"/>
          </svg>
        </span>
        <span v-if="isLockedByPermission" class="lock-badge" :title="t('kanban.column.columnLocked')">🔒</span>
        <span class="card-count">{{ cards.length }}</span>
        <!-- Botão de adicionar task/lead (só na coluna Backlog) -->
        <button 
          v-if="column.nome.toLowerCase().includes('backlog') && canCreateCardsInBacklog"
          class="add-header-btn"
          @click="addCard"
          :title="workspaceType === 'TAREFAS' ? t('kanban.column.addTask') : t('kanban.column.addLead')"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
        </button>
      </div>
      <!-- Menu dropdown (apenas contexto) -->
      <div 
        v-if="showColumnMenu && canManageColumns" 
        class="column-menu"
        :style="{ 
          position: 'fixed', 
          left: menuPosition.x + 'px', 
          top: menuPosition.y + 'px',
          zIndex: 99999
        }"
        @click.stop
        @mousedown.stop
      >
        <button @click="editColumn" class="menu-item" v-if="canManageColumns">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
          </svg>
          {{ t('common.edit') }}
        </button>
        <button @click="deleteColumn" class="menu-item menu-danger" v-if="canDeleteColumns">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
          </svg>
          {{ t('common.delete') }}
        </button>
      </div>
      <!-- Botão Minimizar (só aparece quando expandido) -->
      <button 
        v-if="!isMinimized"
        class="minimize-btn"
        @click="toggleMinimize"
        :title="t('kanban.column.minimize')"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      
      <!-- Botão Maximizar (só aparece quando minimizado) -->
      <button 
        v-if="isMinimized"
        class="maximize-btn"
        @click="toggleMinimize"
        :title="isLockedByPermission ? t('kanban.column.locked') : t('kanban.column.maximize')"
      >
        <svg v-if="!isLockedByPermission" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
        </svg>
        <span v-else>🔒</span>
      </button>
      
      <!-- título vertical no modo minimizado -->
      <div v-if="isMinimized" class="column-title-vertical">
        <span class="vertical-text">{{ column.nome }}</span>
        <span
          v-if="hasColumnUnreadMessages"
          class="column-unread-indicator column-unread-indicator--minimized"
          :title="columnUnreadTitle"
          :aria-label="columnUnreadTitle"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
            <path d="M7 9h10v2H7zm0-3h7v2H7z"/>
          </svg>
        </span>
        <div class="color-line" ></div>
        <span class="card-count-vertical">{{ cards.length }}</span>
      </div>
    </div>
    
    <!-- Cards da Coluna -->
    <div 
      v-if="!isMinimized"
      ref="cardsContainer"
      class="column-cards"
      :data-column-id="column.id"
    >
      <draggable
        v-model="localCards"
        item-key="id"
        :group="{ name: 'kanban-cards', pull: true, put: true }"
        :animation="150"
        :direction="'vertical'"
        class="dnd-list"
        ghost-class="drag-ghost"
        chosen-class="drag-chosen"
        drag-class="drag-dragging"
        :force-fallback="!isMobileViewport"
        :fallback-on-body="!isMobileViewport"
        :delay="touchDragDelay"
        :delay-on-touch-only="true"
        :touch-start-threshold="touchDragThreshold"
        :empty-insert-threshold="40"
        :sort="true"
        :move="onMove"
        :disabled="!canMoveCards"
        @start="onDragStart"
        @unchoose="onUnchoose"
        @add="onAdd"
        @update="onUpdate"
        @change="onChange"
        @end="onDragEnd"
      >
        <template #item="{ element, index }">
          <div>
            <div
              class="card-item"
              :style="{ cursor: 'grab', userSelect: 'none' }"
              @dragstart.prevent
              @touchstart.passive="onCardTouchStart"
              @touchend.passive="onCardTouchEnd"
              @touchcancel.passive="onCardTouchEnd"
            >
            <div v-if="workspaceType === 'TAREFAS'">
              <TaskCard
                :task="element"
                :show-description="showDescription"
                :show-tags-expanded="showTagsExpanded"
                :show-users="showUsers"
                :show-activity-controls="showActivityControls"
                :show-schedule="showSchedule"
                :show-checklist="showChecklist"
                :show-attachments="showAttachments"
                :show-financeiro="showFinanceiro"
                :show-task-progress="showTaskProgress"
                :show-created-at="showCreatedAt"
                :show-updated-at="showUpdatedAt"
                :workspace-tags="workspaceTags"
                :workspace-users="workspaceUsers"
                :workspace-motivos="workspaceMotivos"
                :can-archive-cards="canArchiveCards"
                @update="handleUpdateCard"
                @delete="handleDeleteCard"
                @open-detail="handleCardDetail"
              />
            </div>
            <div v-else>
              <LeadCard
                :lead="element"
                :show-tags-expanded="showTagsExpanded"
                :show-contact-details="showContactDetails"
                :show-business-info="showBusinessInfo"
                :show-users="showUsers"
                :show-schedule="showSchedule"
                :show-attachments="showAttachments"
                :show-created-at="showCreatedAt"
                :show-updated-at="showUpdatedAt"
                :workspace-tags="workspaceTags"
                :workspace-users="workspaceUsers"
                :workspace-motivos="workspaceMotivos"
                :can-edit-lead="canEditLead"
                @update="handleUpdateCard"
                @delete="handleDeleteCard"
                @open-detail="handleCardDetail"
              />
            </div>
            </div>
          </div>
        </template>
      </draggable>


    </div>
    
    <!-- Valor total (apenas para CRM) -->
    <div v-if="workspaceType === 'CRM' && !isMinimized" class="column-total">
      <div class="total-container">
        <div class="total-icon-wrapper">
          <svg class="total-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="total-info">
          <span class="total-label">{{ t('kanban.column.pendingTotal') }}</span>
          <MoneyAmount :value="totalValue" size="sm" class="total-value" />
        </div>
        <div class="total-badge">
          <span class="badge-count">{{ cards.length }}</span>
          <span class="badge-label">{{ cards.length === 1 ? t('kanban.column.lead') : t('kanban.column.leads') }}</span>
        </div>
      </div>
    </div>
    
    <!-- Totais financeiros (TAREFAS - modo financeiro) -->
    <div v-if="workspaceType === 'TAREFAS' && showFinanceiro && !isMinimized" class="column-total">
      <div
        class="total-container total-container-compact total-clickable"
        role="button"
        tabindex="0"
        :title="t('kanban.column.openColumnReceivables')"
        @click="openRecebiveisModal"
        @keydown.enter.prevent="openRecebiveisModal"
        @keydown.space.prevent="openRecebiveisModal"
      >
        <div class="total-leading-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 1v8m0 0v1m0-1a2.5 2.5 0 01-2.5-2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="total-info total-info-compact">
          <div class="total-row">
            <span class="total-label">{{ t('kanban.column.pending') }}</span>
            <MoneyAmount :value="columnRecebiveisStats.valorPendente" size="sm" class="total-value" />
          </div>
          <div class="total-row">
            <span class="total-label">{{ t('kanban.column.received') }}</span>
            <MoneyAmount :value="columnRecebiveisStats.valorRecebido" size="sm" class="total-value total-value-received" />
          </div>
        </div>
        <div class="total-counters" :aria-label="t('kanban.column.columnIndicators')">
          <span class="micro-badge micro-badge--tasks" :title="`${cards.length} ${cards.length === 1 ? t('kanban.column.task') : t('kanban.column.tasks')}`">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
            {{ cards.length }}
          </span>
          <span class="micro-badge micro-badge--recebiveis" :title="`${columnRecebiveisStats.quantidadeRecebiveis} ${columnRecebiveisStats.quantidadeRecebiveis === 1 ? t('kanban.column.receivable') : t('kanban.column.receivables')}`">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 1v8m0 0v1m0-1a2.5 2.5 0 01-2.5-2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            {{ columnRecebiveisStats.quantidadeRecebiveis }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="showRecebiveisModal"
      class="recebiveis-modal-overlay"
      @click.self="closeRecebiveisModal"
    >
      <div class="recebiveis-modal">
        <div class="modal-header flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 pr-12 sm:pr-3 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-secondary)] recebiveis-modal-header">
          <div class="recebiveis-modal-heading">
            <div class="recebiveis-header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 1v8m0 0v1m0-1a2.5 2.5 0 01-2.5-2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <span class="recebiveis-header-dot" aria-hidden="true"></span>
            <div class="recebiveis-modal-title-wrap">
              <h4>{{ t('kanban.column.columnReceivablesTitle') }}</h4>
              <p>{{ column.nome }}</p>
            </div>
          </div>
          <button class="recebiveis-close-btn" @click="closeRecebiveisModal" :aria-label="t('common.close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="recebiveis-modal-stats">
          <div class="recebiveis-stat">
            <span>{{ t('kanban.column.totalPending') }}</span>
            <strong><MoneyAmount :value="columnRecebiveisStats.valorPendente" size="sm" /></strong>
          </div>
          <div class="recebiveis-stat">
            <span>{{ t('kanban.column.totalReceived') }}</span>
            <strong><MoneyAmount :value="columnRecebiveisStats.valorRecebido" size="sm" /></strong>
          </div>
          <div class="recebiveis-stat">
            <span>{{ t('kanban.column.receivablesLabel') }}</span>
            <strong>{{ columnRecebiveisLinhas.length }}</strong>
          </div>
        </div>

        <div class="recebiveis-modal-list">
          <div v-if="columnRecebiveisLinhas.length === 0" class="recebiveis-empty">
            {{ t('kanban.column.noReceivablesInColumn') }}
          </div>
          <div v-else class="recebiveis-lines">
            <div v-for="item in columnRecebiveisLinhas" :key="item.id" class="recebivel-line">
              <div class="recebivel-main">
                <span class="recebivel-task" :title="item.taskTitle">{{ item.taskTitle }}</span>
                <span class="recebivel-date">{{ formatDateOnly(item.dataVencimento) }}</span>
              </div>
              <div class="recebivel-side">
                <MoneyAmount :value="item.valor" size="sm" class="recebivel-value" />
                <span class="recebivel-status" :class="item.statusClass">{{ item.statusLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
  import TaskCard from './TaskCard.vue'
  import LeadCard from './LeadCard.vue'
  import MoneyAmount from './MoneyAmount.vue'
  import Draggable from 'vuedraggable'
import api from '@/services/api'

export default {
  name: 'KanbanColumn',
         components: {
           TaskCard,
           LeadCard,
           MoneyAmount,
           draggable: Draggable
         },
  props: {
    column: {
      type: Object,
      required: true
    },
    workspaceType: {
      type: String,
      required: true
    },
    cards: {
      type: Array,
      default: () => []
    },
    showDescription: {
      type: Boolean,
      default: false
    },
    showTagsExpanded: {
      type: Boolean,
      default: false
    },
    showUsers: {
      type: Boolean,
      default: false
    },
    showActivityControls: {
      type: Boolean,
      default: false
    },
    showSchedule: {
      type: Boolean,
      default: false
    },
    showChecklist: {
      type: Boolean,
      default: false
    },
    showAttachments: {
      type: Boolean,
      default: false
    },
    showContactDetails: {
      type: Boolean,
      default: false
    },
    showBusinessInfo: {
      type: Boolean,
      default: false
    },
    showFinanceiro: {
      type: Boolean,
      default: false
    },
    showTaskProgress: {
      type: Boolean,
      default: false
    },
    showCreatedAt: {
      type: Boolean,
      default: false
    },
    showUpdatedAt: {
      type: Boolean,
      default: false
    },
    workspaceTags: {
      type: Array,
      default: () => []
    },
    workspaceUsers: {
      type: Array,
      default: () => []
    },
    workspaceMotivos: {
      type: Array,
      default: () => []
    },
    isLockedByPermission: {
      type: Boolean,
      default: false
    },
    canMoveColumns: {
      type: Boolean,
      default: true
    },
    canMoveCards: {
      type: Boolean,
      default: true
    },
    canCreateCardsInBacklog: {
      type: Boolean,
      default: true
    },
    canArchiveCards: {
      type: Boolean,
      default: true
    },
    canManageColumns: {
      type: Boolean,
      default: true
    },
    canDeleteColumns: {
      type: Boolean,
      default: true
    },
    canEditLead: {
      type: Boolean,
      default: true
    }
  },
    emits: ['move-card', 'reorder-card-local', 'update-card', 'delete-card', 'edit-column', 'delete-column', 'card-detail', 'add-task', 'add-lead', 'drag-start', 'drag-end'],
  setup(props, { emit }) {
    const { t, locale } = useI18n()
    const showColumnMenu = ref(false)
    const menuPosition = ref({ x: 0, y: 0 })
    const totalValue = ref(0)
    const showRecebiveisModal = ref(false)
    const isMobileViewport = ref(false)

    const updateMobileViewport = () => {
      isMobileViewport.value = window.innerWidth <= 768
    }

    const touchDragDelay = computed(() => (isMobileViewport.value ? 2000 : 0))
    const touchDragThreshold = computed(() => (isMobileViewport.value ? 12 : 4))
    
    const parseRecebiveisCard = (card) => {
      try {
        if (Array.isArray(card?.recebiveis)) return card.recebiveis
        if (typeof card?.recebiveis === 'string') return JSON.parse(card.recebiveis)
        return []
      } catch {
        return []
      }
    }

    // Computed: totais de recebíveis da coluna (modo financeiro)
    const columnRecebiveisStats = computed(() => {
      if (props.workspaceType !== 'TAREFAS' || !props.showFinanceiro) {
        return { quantidadeRecebiveis: 0, valorPendente: 0, valorRecebido: 0 }
      }
      return (props.cards || []).reduce((acc, card) => {
        const recebiveis = parseRecebiveisCard(card)
        recebiveis.forEach((item) => {
          const valor = parseFloat(item?.valor) || 0
          acc.quantidadeRecebiveis += 1
          if (item?.pago) acc.valorRecebido += valor
          else acc.valorPendente += valor
        })
        return acc
      }, {
        quantidadeRecebiveis: 0,
        valorPendente: 0,
        valorRecebido: 0
      })
    })

    const columnRecebiveisLinhas = computed(() => {
      const linhas = []
      ;(props.cards || []).forEach((card) => {
        const recebiveis = parseRecebiveisCard(card)
        const taskTitle = String(card?.titulo || card?.nome || `Task #${card?.id || ''}`).trim()
        recebiveis.forEach((item, index) => {
          const valor = parseFloat(item?.valor) || 0
          const status = getRecebivelStatus(item)
          linhas.push({
            id: item?.id || `${card?.id || 'task'}_${index}`,
            taskTitle,
            valor,
            dataVencimento: item?.dataVencimento || null,
            statusLabel: status.label,
            statusClass: status.className
          })
        })
      })
      return linhas.sort((a, b) => String(a.dataVencimento || '').localeCompare(String(b.dataVencimento || '')))
    })
    
    // 💾 Função para carregar estado de minimização do localStorage
    const loadMinimizedState = () => {
      try {
        const storageKey = `kanban_column_minimized_ws${props.column.workspaceId}_col${props.column.id}`
        const saved = localStorage.getItem(storageKey)
        return saved === 'true'
      } catch (e) {
        console.error('Erro ao carregar estado de minimização:', e)
        return false
      }
    }
    
    const isMinimized = ref(loadMinimizedState())
    const cardsContainer = ref(null)
           const isDragActive = ref(false)
    const dragOverIndex = ref(-1)
    const draggingCardId = ref(null)
    const draggingFromColumnId = ref(null)
          const localCards = ref([])
          const isClickSuppressed = ref(false)
          const lastPointer = ref({ x: 0, y: 0 })
          const autoScrollActive = ref(false)
          let autoScrollRAF = null
          let pointerTrackingCleanup = null

          const trackPointerPosition = (event) => {
            const point = event.touches?.[0] ?? event.changedTouches?.[0] ?? event
            if (point?.clientX == null || point?.clientY == null) return
            lastPointer.value = { x: point.clientX, y: point.clientY }
          }

          const startPointerTracking = () => {
            stopPointerTracking()
            document.addEventListener('touchmove', trackPointerPosition, { passive: true })
            document.addEventListener('mousemove', trackPointerPosition, { passive: true })
            pointerTrackingCleanup = () => {
              document.removeEventListener('touchmove', trackPointerPosition)
              document.removeEventListener('mousemove', trackPointerPosition)
            }
          }

          const stopPointerTracking = () => {
            pointerTrackingCleanup?.()
            pointerTrackingCleanup = null
          }

          const runBoardAutoScroll = () => {
            const board = document.querySelector('.kanban-board')
            const x = lastPointer.value.x
            if (!board || x <= 0) return

            const boardRect = board.getBoundingClientRect()
            const thresholdX = isMobileViewport.value
              ? Math.max(52, Math.floor(boardRect.width * 0.14))
              : Math.max(1, Math.floor(boardRect.width * 0.20))
            const speedX = isMobileViewport.value ? 24 : 14
            const maxScrollLeft = Math.max(0, board.scrollWidth - board.clientWidth)

            if (x - boardRect.left < thresholdX) {
              board.scrollLeft = Math.max(0, board.scrollLeft - speedX)
            } else if (boardRect.right - x < thresholdX) {
              board.scrollLeft = Math.min(maxScrollLeft, board.scrollLeft + speedX)
            }
          }

          const runColumnAutoScroll = () => {
            const x = lastPointer.value.x
            const y = lastPointer.value.y
            if (y <= 0) return

            let container = null
            const pointEl = (typeof document !== 'undefined' && document.elementFromPoint)
              ? document.elementFromPoint(x, y)
              : null
            container = pointEl?.closest?.('.column-cards') || null

            if (!container) {
              const columns = document.querySelectorAll('.kanban-column:not(.minimized) .column-cards')
              for (const columnEl of columns) {
                const columnRect = columnEl.getBoundingClientRect()
                if (x >= columnRect.left && x <= columnRect.right && y >= columnRect.top && y <= columnRect.bottom) {
                  container = columnEl
                  break
                }
              }
            }

            if (!container) {
              container = cardsContainer.value
            }
            if (!container) return

            const rect = container.getBoundingClientRect()
            const thresholdY = Math.max(1, Math.floor(rect.height * 0.20))
            const speedY = isMobileViewport.value ? 16 : 12

            if (y - rect.top < thresholdY) {
              container.scrollTop = Math.max(0, container.scrollTop - speedY)
            } else if (rect.bottom - y < thresholdY) {
              container.scrollTop = container.scrollTop + speedY
            }
          }

          const autoScrollLoop = () => {
            if (!autoScrollActive.value) return
            try {
              runBoardAutoScroll()
              runColumnAutoScroll()
            } catch {}
            autoScrollRAF = requestAnimationFrame(autoScrollLoop)
          }

          const getKanbanBoard = () => document.querySelector('.kanban-board')

          const lockBoardHorizontalScroll = () => {
            if (!isMobileViewport.value) return
            getKanbanBoard()?.classList.add('kanban-board--lock-horizontal')
          }

          const releaseBoardHorizontalScroll = () => {
            getKanbanBoard()?.classList.remove('kanban-board--lock-horizontal')
          }

          const onCardTouchStart = () => {
            lockBoardHorizontalScroll()
          }

          const onCardTouchEnd = () => {
            window.setTimeout(() => {
              if (!isDragActive.value) {
                releaseBoardHorizontalScroll()
              }
            }, 80)
          }
           // Sincroniza lista local com a prop cards para o vuedraggable
           localCards.value = Array.isArray(props.cards) ? [...props.cards] : []

           watch(() => props.cards, (val) => {
             localCards.value = Array.isArray(val) ? [...val] : []
           }, { deep: true })
           watch(() => props.isLockedByPermission, (locked) => {
             if (locked) {
               isMinimized.value = true
               try {
                 const storageKey = `kanban_column_minimized_ws${props.column.workspaceId}_col${props.column.id}`
                 localStorage.setItem(storageKey, 'true')
               } catch {}
             }
           })
           // Handlers do vuedraggable
           const onChange = (evt) => {
             if (evt?.added) {
               const { newIndex, element } = evt.added
               if (element) emit('move-card', element.id, props.column.id, newIndex)
             } else if (evt?.moved) {
               const { newIndex, element } = evt.moved
               if (element) emit('reorder-card-local', element.id, props.column.id, newIndex)
             }
           }

           // Eventos específicos do Sortable (vuedraggable)
           const onAdd = (evt) => {
             try {
               const idx = typeof evt?.newIndex === 'number' ? evt.newIndex : (dragOverIndex.value >= 0 ? dragOverIndex.value : (localCards.value.length - 1))
               const inserted = localCards.value[idx]
               if (inserted?.id) {
                 emit('move-card', inserted.id, props.column.id, idx)
               }
             } catch (e) {
               console.error('onAdd error', e)
             }
           }

           const onUpdate = (evt) => {
             try {
               const idx = typeof evt?.newIndex === 'number' ? evt.newIndex : -1
               if (idx >= 0) {
                 const el = localCards.value[idx]
                 if (el?.id) emit('reorder-card-local', el.id, props.column.id, idx)
               }
             } catch (e) {
               console.error('onUpdate error', e)
             }
           }

           const onUnchoose = () => {
             window.setTimeout(() => {
               if (!isDragActive.value) {
                 releaseBoardHorizontalScroll()
               }
             }, 80)
           }

           const onDragEnd = () => {
             isDragActive.value = false
             dragOverIndex.value = -1
             draggingCardId.value = null
             stopPointerTracking()
             document.body.classList.remove('kanban-mobile-card-drag')
             releaseBoardHorizontalScroll()
             // parar auto-scroll e reabilitar clique após pequeno atraso
             if (autoScrollRAF) {
               cancelAnimationFrame(autoScrollRAF)
               autoScrollRAF = null
             }
             autoScrollActive.value = false
             setTimeout(() => {
               isClickSuppressed.value = false
             }, 200)
           }
  const draggingCardHeight = ref(0)
  
  const onDragStart = (evt) => {
    isDragActive.value = true
    isClickSuppressed.value = true
    autoScrollActive.value = true
    try {
      const ev = evt?.originalEvent
      const touch = ev?.touches?.[0] ?? ev?.changedTouches?.[0]
      if (touch) {
        lastPointer.value = { x: touch.clientX ?? 0, y: touch.clientY ?? 0 }
      } else if (ev) {
        lastPointer.value = { x: ev.clientX ?? 0, y: ev.clientY ?? 0 }
      }
    } catch {}
    startPointerTracking()
    if (isMobileViewport.value) {
      document.body.classList.add('kanban-mobile-card-drag')
      lockBoardHorizontalScroll()
    }
    try {
      const rect = evt?.item?.getBoundingClientRect?.()
      draggingCardHeight.value = Math.max(40, Math.round(rect?.height || 56))
    } catch {}
    dragOverIndex.value = evt?.oldIndex ?? -1

    if (autoScrollRAF) {
      cancelAnimationFrame(autoScrollRAF)
      autoScrollRAF = null
    }
    autoScrollRAF = requestAnimationFrame(autoScrollLoop)
  }

  const onMove = (evt) => {
    if (!props.canMoveCards || props.isLockedByPermission) {
      return false
    }
    // Atualiza o índice de preview durante o arrasto
    try {
      // Garantir que o placeholder apareça também em outras colunas
      isDragActive.value = true
      try {
        const ev = evt?.originalEvent
        if (ev) {
          lastPointer.value = { x: ev.clientX ?? lastPointer.value.x, y: ev.clientY ?? lastPointer.value.y }
        }
      } catch {}
      // altura do item sendo arrastado
      try {
        const h = evt?.draggedRect?.height || evt?.dragged?.getBoundingClientRect?.()?.height
        if (h) draggingCardHeight.value = Math.max(40, Math.round(h))
      } catch {}

      const listLen = evt?.relatedContext?.list?.length ?? 0
      const idx = evt?.relatedContext?.index
      let nextIndex
      if (typeof idx === 'number' && idx >= 0) {
        nextIndex = evt?.willInsertAfter ? idx + 1 : idx
      } else {
        nextIndex = listLen
      }
      // clamp
      if (nextIndex < 0) nextIndex = 0
      if (nextIndex > listLen) nextIndex = listLen
      dragOverIndex.value = nextIndex
    } catch {}
    return true
  }
    
    const columnUnreadCount = computed(() => {
      if (props.workspaceType !== 'CRM') return 0
      return (props.cards || []).reduce((sum, card) => sum + (Number(card?.mensagensNaoLidas) || 0), 0)
    })

    const hasColumnUnreadMessages = computed(() => columnUnreadCount.value > 0)

    const columnUnreadTitle = computed(() => {
      const count = columnUnreadCount.value
      if (count <= 0) return ''
      if (count === 1) return t('kanban.column.unreadMessagesOne')
      return t('kanban.column.unreadMessagesMany', { count })
    })

    // Computed
    const isDefaultColumn = computed(() => props.column.isPadrao)
    
    // Métodos antigos removidos - usando HTML5 drag and drop
    
    const handleUpdateCard = (cardId, updates) => {
      emit('update-card', cardId, updates)
    }
    
    const handleDeleteCard = (cardId) => {
      emit('delete-card', cardId)
    }
    
    const handleCardDetail = (card) => {
      if (isClickSuppressed.value) return
      emit('card-detail', card)
    }
    
    const editColumn = () => {
      console.log('🖱️ Botão editar clicado para coluna:', props.column.id)
      showColumnMenu.value = false
      emit('edit-column', props.column.id)
    }
    
    const deleteColumn = () => {
      console.log('🖱️ Botão deletar clicado para coluna:', props.column.id)
      showColumnMenu.value = false
      emit('delete-column', props.column.id)
    }
    
    const handleRightClick = (event) => {
      if (!props.canManageColumns) {
        return
      }
      event.preventDefault()
      
      // Capturar posição do mouse
      menuPosition.value = {
        x: event.clientX,
        y: event.clientY
      }
      
      // Emitir evento global para fechar outros menus
      window.dispatchEvent(new CustomEvent('close-column-menus', { 
        detail: { columnId: props.column.id } 
      }))
      showColumnMenu.value = true
    }
    
    const closeMenuOnClickOutside = (event) => {
      if (showColumnMenu.value && !event.target.closest('.column-menu') && !event.target.closest('.column-header')) {
        showColumnMenu.value = false
      }
    }
    
    const addCard = () => {
      // Emitir evento para abrir modal de criação baseado no tipo de workspace
      if (props.workspaceType === 'TAREFAS') {
        emit('add-task', props.column.id)
      } else if (props.workspaceType === 'CRM') {
        emit('add-lead', props.column.id)
      }
    }
    
    const editCard = (card) => {
      console.log('Editar card:', card.id)
      emit('card-detail', card)
    }

    const handleMouseDown = (e) => {
      console.log('🖱️ Mouse down on card', e.target)
      // Permitir que o drag funcione
    }

    const handleCardClick = (card, event) => {
      // Só abrir o modal se não foi um drag
      if (!isDragActive.value) {
        console.log('🖱️ Card click (not drag):', card.id)
        editCard(card)
      }
    }
    
    const dateLocale = computed(() => {
      if (locale.value === 'en') return 'en-US'
      if (locale.value === 'es') return 'es'
      return 'pt-BR'
    })

    const formatDateOnly = (dateInput) => {
      if (!dateInput) return '--'
      try {
        const parsed = parseDateOnlyValue(dateInput)
        if (Number.isNaN(parsed.getTime())) return '--'
        return parsed.toLocaleDateString(dateLocale.value)
      } catch {
        return '--'
      }
    }

    const parseDateOnlyValue = (dateInput) => {
      if (!dateInput) return new Date('invalid')
      if (Array.isArray(dateInput) && dateInput.length >= 3) {
        const [ano, mes, dia] = dateInput
        return new Date(Number(ano), Number(mes) - 1, Number(dia))
      }
      if (typeof dateInput === 'string') {
        const normalized = String(dateInput).trim()
        if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
          return new Date(`${normalized}T00:00:00`)
        }
        // fallback para strings ISO maiores
        if (normalized.includes('T')) {
          return new Date(`${normalized.split('T')[0]}T00:00:00`)
        }
        // fallback para dd/mm/yyyy
        const brMatch = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
        if (brMatch) {
          const [, dia, mes, ano] = brMatch
          return new Date(Number(ano), Number(mes) - 1, Number(dia))
        }
      }
      return new Date(dateInput)
    }

    const getRecebivelStatus = (item) => {
      if (item?.pago) return { label: t('kanban.column.statusPaid'), className: 'status-paid' }
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)
      const venc = parseDateOnlyValue(item?.dataVencimento)
      if (Number.isNaN(venc.getTime())) return { label: t('kanban.column.statusOpen'), className: 'status-open' }
      const diffDias = Math.ceil((venc.getTime() - hoje.getTime()) / 86400000)
      if (diffDias < 0) return { label: t('kanban.column.statusOverdue'), className: 'status-overdue' }
      if (diffDias <= 5) return { label: t('kanban.column.statusDueSoon'), className: 'status-near' }
      return { label: t('kanban.column.statusOpen'), className: 'status-open' }
    }

    const openRecebiveisModal = () => {
      showRecebiveisModal.value = true
    }

    const closeRecebiveisModal = () => {
      showRecebiveisModal.value = false
    }

    const toggleMinimize = () => {
      if (isMinimized.value && props.isLockedByPermission) {
        return
      }
      isMinimized.value = !isMinimized.value
      showColumnMenu.value = false
      
      // 💾 Salvar estado no localStorage
      try {
        const storageKey = `kanban_column_minimized_ws${props.column.workspaceId}_col${props.column.id}`
        localStorage.setItem(storageKey, isMinimized.value.toString())
        console.log(`💾 Estado da coluna ${props.column.nome} salvo:`, isMinimized.value)
      } catch (e) {
        console.error('Erro ao salvar estado de minimização:', e)
      }
    }
    
    const loadTotalValue = async () => {
      if (props.workspaceType === 'CRM') {
        try {
          // Evitar requisições desnecessárias se não há cards
          if (!props.cards || props.cards.length === 0) {
            totalValue.value = 0
            return
          }
          
          const response = await api.get(`/leads/valor-total/coluna/${props.column.id}`)
          const newValue = response.data.valorTotal || 0
          
          // Só atualizar se o valor realmente mudou
          if (totalValue.value !== newValue) {
            totalValue.value = newValue
            console.log('💰 Total atualizado para coluna', props.column.id, ':', newValue)
          }
        } catch (error) {
          console.error('Erro ao carregar valor total:', error)
        }
      }
    }
    
    // HTML5 Drag and Drop nativo
    const handleDragStart = (card, index, event) => {
      console.log('🚀 HTML5 Drag started:', { 
        cardId: card.id, 
        fromColumn: props.column.id,
        cardType: props.workspaceType,
        event: event.type
      })
      
      draggingCardId.value = card.id
      isDragActive.value = true
    // medir altura do card para o placeholder
    try {
      const target = event.currentTarget
      const rect = target?.getBoundingClientRect?.()
      draggingCardHeight.value = Math.max(40, Math.round(rect?.height || 56))
    } catch {}
      
      // Configurar dados do drag
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('application/json', JSON.stringify({
        cardId: card.id,
        fromColumn: props.column.id,
        fromIndex: index,
        cardType: props.workspaceType === 'TAREFAS' ? 'task' : 'lead'
      }))
      
      console.log('📦 Drag data configurado:', {
        cardId: card.id,
        fromColumn: props.column.id,
        fromIndex: index
      })
      
      // Emitir evento para o pai
      emit('drag-start', card, props.column.id)
    }

    const handleDragEnd = () => {
      console.log('🎯 HTML5 Drag ended')
      draggingCardId.value = null
      isDragActive.value = false
      dragOverIndex.value = -1
    draggingCardHeight.value = 0
      emit('drag-end')
    }

    const handleDragOver = (event) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      
      // Só calcular posição se estivermos arrastando
      if (!isDragActive.value) return
      
      const container = cardsContainer.value
      if (!container) return
      
      const cards = Array.from(container.querySelectorAll('.card-item'))
      const mouseY = event.clientY
      let insertIndex = cards.length
      
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i]
        const rect = card.getBoundingClientRect()
        const cardMiddle = rect.top + rect.height / 2
        
        if (mouseY < cardMiddle) {
          insertIndex = i
          break
        }
      }
      
      dragOverIndex.value = insertIndex
      console.log('📍 Posição calculada:', insertIndex)
    }

  const handleDragEnter = (event) => {
    // quando entra em outra coluna, ativa preview
    isDragActive.value = true
    // se coluna estiver vazia, posiciona no final (0)
    if (!cardsContainer.value || props.cards.length === 0) {
      dragOverIndex.value = 0
    }
  }

  const handleDragLeave = (event) => {
    // só limpa se saiu completamente da coluna
    const related = event.relatedTarget
    if (!cardsContainer.value?.contains(related)) {
      dragOverIndex.value = -1
    }
  }

    const handleDrop = (event) => {
      event.preventDefault()
      console.log('🎯 HTML5 Drop event na coluna:', props.column.id)
      
      try {
        const data = JSON.parse(event.dataTransfer.getData('application/json'))
        console.log('📦 Drop data recebido:', data)
        
        const { cardId, fromColumn, cardType } = data
        
        console.log('📊 Detalhes do drop:', {
          cardId,
          fromColumn,
          toColumn: props.column.id,
          cardType
        })
        
        // RECALCULAR posição exata no momento do drop baseado na posição do mouse
        const container = cardsContainer.value
        let calculatedIndex = props.cards.length // Padrão: final da lista
        
        if (container) {
          const cards = Array.from(container.querySelectorAll('.card-item'))
          const mouseY = event.clientY
          
          for (let i = 0; i < cards.length; i++) {
            const card = cards[i]
            const rect = card.getBoundingClientRect()
            const cardMiddle = rect.top + (rect.height / 2)
            
            if (mouseY < cardMiddle) {
              calculatedIndex = i
              break
            }
          }
        }
        
        console.log('📍 Índice calculado no drop:', calculatedIndex)
        
        // Entre colunas x mesma coluna
        if (fromColumn !== props.column.id) {
          // Inserir no índice calculado nesta coluna
          let newIndex = calculatedIndex
          if (newIndex > props.cards.length) newIndex = props.cards.length
          // Forçar exibir placeholder na coluna destino
          isDragActive.value = true
          console.log('🔄 Card movido para outra coluna:', { toColumn: props.column.id, newIndex })
          emit('move-card', cardId, props.column.id, newIndex)
        } else {
          // Reordenação local na mesma coluna (estilo Trello)
          const newIndex = Math.max(0, Math.min(calculatedIndex, props.cards.length - 1))
          const currentIndex = props.cards.findIndex(c => c.id === cardId)
          if (newIndex !== -1 && newIndex !== currentIndex) {
            console.log('🔄 Reordenando localmente dentro da coluna:', { from: currentIndex, to: newIndex })
            emit('reorder-card-local', cardId, props.column.id, newIndex)
          } else {
            console.log('ℹ️ Sem mudança de posição')
          }
        }
      } catch (error) {
        console.error('❌ Erro ao processar drop:', error)
      }
    }

    // Watch para recalcular total quando cards mudarem (CRM)
    // Usar debounce para evitar múltiplas chamadas desnecessárias
    let totalCalculationTimeout = null
    watch(() => props.cards, () => {
      if (props.workspaceType === 'CRM') {
        // Debounce para evitar múltiplas chamadas
        if (totalCalculationTimeout) {
          clearTimeout(totalCalculationTimeout)
        }
        totalCalculationTimeout = setTimeout(() => {
          console.log('🔄 Cards mudaram na coluna', props.column.id, '- Recalculando total...')
          loadTotalValue()
        }, 300) // 300ms de delay
      }
    }, { deep: true })
    
    // Lifecycle
    onMounted(async () => {
      updateMobileViewport()
      window.addEventListener('resize', updateMobileViewport)
      if (props.isLockedByPermission) {
        isMinimized.value = true
      }
      loadTotalValue()
      document.addEventListener('click', closeMenuOnClickOutside)

      // Listener para fechar menu quando outros abrem
      const handleCloseMenus = (event) => {
        if (event.detail.columnId !== props.column.id) {
          showColumnMenu.value = false
        }
      }

      window.addEventListener('close-column-menus', handleCloseMenus)

      // HTML5 Drag and Drop não precisa de inicialização
      console.log('🔧 HTML5 Drag and Drop configurado para coluna:', props.column.id)
    })
    
    onUnmounted(() => {
      stopPointerTracking()
      document.body.classList.remove('kanban-mobile-card-drag')
      releaseBoardHorizontalScroll()
      if (autoScrollRAF) {
        cancelAnimationFrame(autoScrollRAF)
        autoScrollRAF = null
      }
      window.removeEventListener('resize', updateMobileViewport)
      document.removeEventListener('click', closeMenuOnClickOutside)
      window.removeEventListener('close-column-menus', () => {})
    })
    
    return {
      t,
      showColumnMenu,
      menuPosition,
      totalValue,
      columnRecebiveisStats,
      columnRecebiveisLinhas,
      showRecebiveisModal,
      isMinimized,
      cardsContainer,
      isDragActive,
      dragOverIndex,
      draggingCardId,
      draggingFromColumnId,
      localCards,
      draggingCardHeight,
      isDefaultColumn,
      hasColumnUnreadMessages,
      columnUnreadTitle,
      isMobileViewport,
      touchDragDelay,
      touchDragThreshold,
      toggleMinimize,
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      onChange,
      onDragEnd,
      onUnchoose,
      onCardTouchStart,
      onCardTouchEnd,
      onDragStart,
      onMove,
      onAdd,
      onUpdate,
      handleDrop,
      handleUpdateCard,
      handleDeleteCard,
      handleCardDetail,
      editColumn,
      deleteColumn,
      handleRightClick,
      addCard,
      editCard,
      formatDateOnly,
      openRecebiveisModal,
      closeRecebiveisModal
    }
  }
}
</script>

<style scoped>
.dnd-list {
  min-height: 56px; /* garante zona de drop quando vazio */
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

/* Margem inferior pequena entre os cards */
.dnd-list > div {
  margin-bottom: 6px;
}

.dnd-list > div:last-child {
  margin-bottom: 0;
}

.drag-ghost {
  opacity: 0.4;
  pointer-events: none;
}

.drag-chosen {
  transform: scale(1.01);
  cursor: grabbing !important;
}

.drag-dragging {
  z-index: 9999;
  pointer-events: none;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
}

/* Sortable fallback clone - evita conflito com mouse events no Chrome/Edge */
.sortable-fallback {
  pointer-events: none !important;
  opacity: 0.85;
  z-index: 9999 !important;
  cursor: grabbing !important;
}

/* Desabilitar hover/animation transforms durante drag ativo */
.sortable-drag {
  pointer-events: none !important;
}
.kanban-column {
  @apply w-64 flex flex-col;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 2px;
  min-width: 256px;
  height: calc(100%);
  margin-top: 0px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.15s ease-out;
  transform-origin: left center;
}

.kanban-column:hover {
  border: 1px dashed var(--border-hover);
}

.kanban-column.minimized {
  @apply w-8;
  min-width: 32px;
  max-width: 32px;
  border-radius: 1px;
  border: 1px solid var(--border-primary);
  height: auto;
  min-height: auto;
  margin-top: 0px;
  background-color: var(--bg-secondary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease-out;
}

.kanban-column.minimized:hover {
  border: 1px dashed var(--border-hover);
}

.column-header {
  @apply flex justify-between items-center px-1.5 py-1;
  background-color: transparent;
  color: var(--text-primary);
  border-bottom: 1px solid var(--column-color);
  box-shadow: none;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.column-title {
  @apply flex items-center gap-1;
}

.column-drag-handle {
  @apply flex items-center justify-center;
  cursor: grab;
  padding: 2px;
  color: white;
  opacity: 0.4;
  transition: opacity 0.2s ease;
  border-radius: 4px;
  user-select: none;
  pointer-events: auto;
  z-index: 10;
}

/* Tema claro - ícone preto */
body.tema_clara .column-drag-handle {
  color: black;
}

.column-drag-handle:hover {
  opacity: 1;
  cursor: grab;
  background: rgba(255, 255, 255, 0.1);
}

.column-drag-handle:active {
  cursor: grabbing;
  background: rgba(255, 255, 255, 0.2);
}

.column-title h3 {
  @apply font-medium text-xs;
  color: var(--text-primary);
}

.card-count {
  @apply text-[9px] px-1.5 py-0.5;
  border-radius: 1px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
}

.lock-badge {
  font-size: 12px;
  opacity: 0.9;
}

.column-unread-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: #ef4444;
  opacity: 0.82;
  pointer-events: none;
}

.column-unread-indicator svg {
  width: 11px;
  height: 11px;
}

.column-unread-indicator--minimized {
  margin: 4px 0 2px;
  opacity: 0.9;
}


.minimize-btn, .maximize-btn {
  @apply p-1 transition-all duration-300 ease-out;
  border-radius: 1px;
  color: var(--text-primary);
  order: 1;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  margin-top: 2px;
  transform-origin: center;
}
.minimize-btn:hover, .maximize-btn:hover { 
  transform: scale(1.1) rotate(5deg);
  color: var(--text-primary);
}
.minimize-btn:active, .maximize-btn:active {
  transform: scale(0.95) rotate(-2deg);
}

.add-header-btn {
  @apply p-1 transition-all duration-200 ease-out;
  border-radius: 1px;
  color: var(--text-secondary);
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  margin-left: 4px;
  opacity: 0.7;
}

.add-header-btn:hover {
  opacity: 1;
  color: var(--text-primary);
  transform: scale(1.1);
}

.add-header-btn:active {
  transform: scale(0.95);
}

.column-menu {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  min-width: 140px;
  backdrop-filter: blur(10px);
  animation: menuSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 99999;
  pointer-events: auto;
  position: fixed;
}

.menu-item {
  @apply flex items-center gap-2 w-full px-3 py-2 transition-all duration-200;
  color: var(--text-primary);
  background: transparent;
  border: none;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  pointer-events: auto;
  z-index: 100000;
}

.menu-item:hover {
  background: var(--bg-secondary);
}

.menu-item.menu-danger:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.column-cards {
  @apply flex-1 space-y-1 overflow-y-auto;
  padding: 0.35rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
  transition: padding 0.25s ease;
  opacity: 1;
  transform: translateY(0);
}

.card-item {
  @apply cursor-grab;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.card-item:active {
  @apply cursor-grabbing;
}

.card-item.dragging {
  opacity: 0.5;
  transform: rotate(5deg) scale(1.05);
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

/* Drag and Drop Styles - Simplificado */

/* Drag and Drop Styles - HTML5 Nativo */


/* Rodapé com Total da Coluna (CRM) - Design Premium */
.column-total {
  margin-top: 0;
  padding: 0;
  border-top: 2px solid var(--border-primary);
  flex-shrink: 0;
  background: var(--bg-primary);
  width: 100%;
}

.total-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%);
  transition: all 0.3s ease;
  width: 100%;
}
.total-container-compact {
  position: relative;
  align-items: stretch;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid rgba(16, 185, 129, 0.18);
  border-radius: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 6px 14px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}
.total-clickable {
  cursor: pointer;
}
.total-container-compact::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, rgba(255,255,255,0.65), rgba(255,255,255,0));
  pointer-events: none;
}
.total-clickable:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 1px;
}

.total-container:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%);
  transform: translateY(-1px);
}

.total-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 8px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.total-icon {
  width: 14px;
  height: 14px;
  color: white;
}

.total-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.total-info-stack {
  gap: 1px;
}
.total-info-compact {
  gap: 4px;
  justify-content: center;
}
.total-leading-icon {
  width: 18px;
  min-width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #10b981;
  opacity: 0.95;
}
.total-leading-icon svg {
  width: 14px;
  height: 14px;
}
.total-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.total-label {
  font-size: 9px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  opacity: 0.9;
}

.total-value {
  font-size: 13px;
  font-weight: 700;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  text-shadow: 0 1px 1px rgba(16, 185, 129, 0.12);
}
.total-value-received {
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 1px 1px rgba(59, 130, 246, 0.12);
}

.total-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 7px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  min-width: 44px;
}
.total-badge-subtle {
  opacity: 0.82;
  padding: 3px 6px;
  min-width: 40px;
}

.badge-count {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.badge-label {
  font-size: 8px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}

.total-meta {
  font-size: 9px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-top: 2px;
  opacity: 0.88;
  text-transform: uppercase;
}
.total-counters {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  min-width: 54px;
}
.micro-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 16px;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.16);
}
.micro-badge svg {
  width: 9px;
  height: 9px;
  stroke-width: 2.1;
}
.micro-badge--tasks {
  color: #4338ca;
  border-color: rgba(99, 102, 241, 0.35);
  background: linear-gradient(135deg, rgba(129, 140, 248, 0.26), rgba(99, 102, 241, 0.1));
}
.micro-badge--recebiveis {
  color: #047857;
  border-color: rgba(16, 185, 129, 0.35);
  background: linear-gradient(135deg, rgba(52, 211, 153, 0.24), rgba(16, 185, 129, 0.1));
}

/* Tema Claro - Total */
.tema_clara .total-container {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.06) 100%);
}

.tema_clara .total-container:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%);
}

.tema_clara .total-badge {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(16, 185, 129, 0.2);
}
.tema_clara .total-container-compact {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(240, 253, 250, 0.92) 100%);
}

/* Tema Escuro - Total */
.tema_escura .total-container {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%);
}
.tema_escura .total-container-compact {
  border-color: rgba(16, 185, 129, 0.32);
  box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.12), 0 10px 18px rgba(2, 6, 23, 0.35);
}
.tema_escura .total-leading-icon {
  color: #34d399;
}
.tema_escura .micro-badge {
  box-shadow: 0 2px 8px rgba(2, 6, 23, 0.5);
}
.tema_escura .micro-badge--tasks {
  color: #c7d2fe;
  border-color: rgba(129, 140, 248, 0.46);
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.48), rgba(59, 130, 246, 0.28));
}
.tema_escura .micro-badge--recebiveis {
  color: #a7f3d0;
  border-color: rgba(52, 211, 153, 0.46);
  background: linear-gradient(135deg, rgba(5, 150, 105, 0.48), rgba(16, 185, 129, 0.28));
}
.tema_escura .recebiveis-header-icon {
  color: #93c5fd;
  border-color: rgba(96, 165, 250, 0.35);
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.36), rgba(99, 102, 241, 0.24));
}
.tema_escura .recebiveis-header-dot {
  background: #60a5fa;
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.2);
}

.recebiveis-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(2, 6, 23, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.recebiveis-modal {
  width: min(760px, 94vw);
  max-height: 88vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.35);
}
.recebiveis-modal-header {
  position: relative;
}
.recebiveis-modal-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.recebiveis-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.18), rgba(99, 102, 241, 0.14));
  border: 1px solid rgba(59, 130, 246, 0.22);
  flex-shrink: 0;
}
.recebiveis-header-icon svg {
  width: 20px;
  height: 20px;
}
.recebiveis-header-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
  flex-shrink: 0;
}
.recebiveis-modal-title-wrap h4 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
}
.recebiveis-modal-title-wrap p {
  margin: 2px 0 0;
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recebiveis-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.recebiveis-close-btn:hover {
  background: rgba(107, 114, 128, 0.12);
  color: var(--text-primary);
}
.recebiveis-close-btn svg {
  width: 14px;
  height: 14px;
}
.recebiveis-modal-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-primary);
}
.recebiveis-stat {
  padding: 8px;
  border-radius: 10px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.recebiveis-stat span {
  font-size: 10px;
  text-transform: uppercase;
  color: var(--text-secondary);
}
.recebiveis-stat strong {
  font-size: 13px;
  color: var(--text-primary);
}
.recebiveis-modal-list {
  overflow-y: auto;
  padding: 10px 14px 14px;
}
.recebiveis-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.recebivel-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  border-radius: 10px;
  padding: 9px 10px;
}
.recebivel-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.recebivel-task {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recebivel-date {
  font-size: 11px;
  color: var(--text-secondary);
}
.recebivel-side {
  display: flex;
  align-items: center;
  gap: 8px;
}
.recebivel-value {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
}
.recebivel-status {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 2px 7px;
  border: 1px solid transparent;
}
.status-paid {
  color: #047857;
  border-color: rgba(5, 150, 105, 0.34);
  background: rgba(16, 185, 129, 0.12);
}
.status-overdue {
  color: #b91c1c;
  border-color: rgba(239, 68, 68, 0.34);
  background: rgba(239, 68, 68, 0.12);
}
.status-near {
  color: #a16207;
  border-color: rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.12);
}
.status-open {
  color: #1e40af;
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.12);
}
.recebiveis-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
  padding: 18px 8px;
}

.tema_escura .total-container:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(5, 150, 105, 0.12) 100%);
}

.tema_escura .total-badge {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(16, 185, 129, 0.2);
}

/* Minimizado */
.kanban-column.minimized .column-header {
  @apply flex-col items-center px-0 py-0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: auto;
  min-height: auto;
  gap: 8px;
  padding: 8px 2px;
}

.column-title-vertical {
  @apply flex flex-col items-center justify-center;
  order: 3;
  flex: 1;
  width: 100%;
  padding: 8px 2px 0 2px;
  min-height: 0;
  opacity: 1;
  visibility: visible;
}

.vertical-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  @apply text-[10px] font-normal;
  color: var(--text-primary);
  line-height: 1.4;
  letter-spacing: 0.8px;
}

.color-line {
  width: calc(100% - 4px);
  height: 2px;
  margin: 12px 2px 8px 2px;
  order: 2;
}

.card-count-vertical {
  @apply text-[8px] px-1 py-0.5;
  border-radius: 1px;
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: var(--text-primary);
  order: 4;
  font-weight: 600;
  min-width: 14px;
  text-align: center;
  margin: 0 2px 6px 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}


/* Animações Keyframes */
@keyframes fadeInContent {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOutContent {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px);
  }
}

@keyframes slideInVertical {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOutVertical {
  0% {
    opacity: 1;
    transform: translateX(0) rotate(90deg);
  }
  100% {
    opacity: 0;
    transform: translateX(-20px) rotate(90deg);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* Estados de animação para colunas minimizadas */
.kanban-column.minimized .column-cards {
  opacity: 0;
  transform: translateY(-20px);
  pointer-events: none;
}

.kanban-column.minimized .column-title {
  opacity: 0;
  transform: translateX(-20px);
}

.kanban-column.minimized .column-actions {
  opacity: 0;
  transform: translateX(-20px);
}

.kanban-column.minimized .column-title-vertical {
  animation: slideInVertical 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 1;
  visibility: visible;
}

.kanban-column.minimized .vertical-text {
  animation: fadeInContent 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both;
  opacity: 1;
  visibility: visible;
  display: block;
}

.kanban-column.minimized .card-count-vertical {
  animation: fadeInContent 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both;
  opacity: 1;
  visibility: visible;
  display: block;
}

/* Estados de animação para colunas expandidas */
.kanban-column:not(.minimized) .column-cards {
  animation: fadeInContent 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.kanban-column:not(.minimized) .column-title {
  animation: fadeInContent 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both;
}

.kanban-column:not(.minimized) .column-actions {
  animation: fadeInContent 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both;
}

.kanban-column:not(.minimized) .column-title-vertical {
  animation: slideOutVertical 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}


/* Animação do botão de minimizar */
.minimize-btn svg {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.kanban-column.minimized .minimize-btn svg {
  transform: rotate(180deg);
}

/* Animação suave para o ícone de seta */
.minimize-btn svg path {
  transition: all 0.3s ease;
}

.minimize-btn:hover svg {
  transform: scale(1.1);
}

.minimize-btn:active svg {
  transform: scale(0.9);
}


/* Animação de entrada suave para cards */
.card-item {
  @apply cursor-pointer;
  transition: all 0.2s ease;
  animation: slideInCard 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.card-item {
  cursor: grab !important;
  user-select: none;
  -webkit-user-drag: none;
  position: relative;
  z-index: 1;
}

.card-item .task-card,
.card-item .lead-card {
  cursor: grab !important;
  -webkit-user-drag: none;
}

/* Impedir que imagens dentro dos cards iniciem drag nativo no Chrome/Edge */
.card-item img,
.card-item a {
  -webkit-user-drag: none;
  user-select: none;
}

.card-item.dragging * {
  pointer-events: none;
  cursor: inherit !important;
}

.card-item:active {
  cursor: grabbing;
}

.card-item.dragging {
  opacity: 0.5;
  transform: rotate(5deg) scale(1.05);
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  cursor: grabbing;
}

.card-item.drag-over {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Placeholder de preview durante drag */
/* Removido placeholder visual - usaremos apenas o ghost do draggable */

@keyframes slideInCard {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Efeito de hover nos cards */
.card-item:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}


/* Animação do total da coluna */
.column-total {
  @apply mt-2  border-t flex justify-between items-center;
  border-color: var(--border-primary);
  animation: fadeInContent 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both;
}

/* Animação de entrada do menu */
@keyframes menuSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 768px) {
  .kanban-column {
    width: 85vw;
    min-width: 85vw;
    max-width: 85vw;
    scroll-snap-align: start;
    border-radius: 8px;
    flex: 0 0 85vw;
  }

  .column-header {
    padding: 6px 8px;
  }

  .column-title h3 {
    font-size: 13px;
    line-height: 1.2;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .card-count {
    font-size: 10px;
    padding: 2px 6px;
  }

  .column-cards {
    padding: 0.45rem;
    touch-action: pan-y;
    overscroll-behavior-x: contain;
  }

  .dnd-list {
    touch-action: pan-y;
    overscroll-behavior-x: contain;
  }

  .card-item {
    cursor: default;
    touch-action: pan-y;
    overscroll-behavior-x: contain;
  }

  .card-item .lead-card,
  .card-item .task-card {
    cursor: pointer;
    touch-action: pan-y;
  }

  :deep(.sortable-drag),
  :deep(.drag-dragging),
  :deep(.sortable-fallback) {
    max-width: 64vw !important;
    width: 64vw !important;
    opacity: 0.94 !important;
    z-index: 10050 !important;
  }

  :deep(.sortable-drag .card-item),
  :deep(.drag-dragging .card-item),
  :deep(.sortable-fallback .card-item) {
    transform: scale(0.78);
    transform-origin: center center;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.28);
  }

  :deep(.sortable-drag .lead-card),
  :deep(.sortable-drag .task-card),
  :deep(.drag-dragging .lead-card),
  :deep(.drag-dragging .task-card),
  :deep(.sortable-fallback .lead-card),
  :deep(.sortable-fallback .task-card) {
    max-height: 108px;
    overflow: hidden;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }

  .kanban-column.minimized {
    width: 44px;
    min-width: 44px;
    max-width: 44px;
  }

  .minimize-btn,
  .maximize-btn {
    width: 24px;
    height: 24px;
  }

  .add-header-btn {
    width: 22px;
    height: 22px;
  }
}
</style>
